import type { UnitCategory } from "verdi-common/Configurations/SensorConfigurations/SensorConfigurationTypes";
import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import {
    AxisSide,
    type AxisSpec,
    type BandSpec,
    PanelType,
    type StreamEntry,
    type TimeAxisPanelSpec,
} from "../ChartSpec";
import type { ResolvedStream } from "./resolve-streams";
import { stableStreamColor } from "./stable-stream-color";
import { type UnitCategoryPresentation, unitCategoryColor, unitCategoryRank } from "./unit-category-presentation";

/** Axis ids are the unit category. The generator and the split/merge transforms both count on that. */
function categoryAxis(unitCategory: string, side: AxisSide): AxisSpec {
    return { id: unitCategory, unitCategory, side };
}

/**
 * The unit categories that go on one panel: everything in `left` shares the left axis, everything
 * in `right` the right one. Categories sharing a side have to share units — the axis takes the
 * first one's. A category belongs to at most one recipe, or it would be drawn on two panels.
 */
export type PanelRecipe = { left: UnitCategory[]; right?: UnitCategory[] };

function recipeCategories(recipe: PanelRecipe): string[] {
    return [...recipe.left, ...(recipe.right ?? [])];
}

/** Unit categories in the order their first stream appears. */
function categoriesOf(streams: StreamEntry[]): string[] {
    return [...new Set(streams.map((stream) => stream.axisId))];
}

/** Same object back when the axis already matches, so untouched entries keep their identity. */
export function withAxisId(stream: StreamEntry, axisId: string): StreamEntry {
    return stream.axisId === axisId ? stream : { ...stream, axisId };
}

export function buildTwoAxisPanel(leftStreams: StreamEntry[], rightStreams: StreamEntry[]): TimeAxisPanelSpec | null {
    if (leftStreams.length === 0 && rightStreams.length === 0) return null;

    const axes: AxisSpec[] = [];
    const streams: StreamEntry[] = [];

    if (leftStreams.length > 0) {
        const axisId = leftStreams[0].axisId;
        axes.push(categoryAxis(axisId, AxisSide.Left));
        streams.push(...leftStreams.map((stream) => withAxisId(stream, axisId)));
    }

    if (rightStreams.length > 0) {
        const axisId = rightStreams[0].axisId;
        axes.push(categoryAxis(axisId, AxisSide.Right));
        streams.push(...rightStreams.map((stream) => withAxisId(stream, axisId)));
    }

    return {
        id: `two-axis-panel-${axes.map((axis) => axis.id).join("-")}`,
        type: PanelType.TimeAxis,
        axes,
        streams,
    };
}

export function buildCategoryPanel(unitCategory: string, streams: StreamEntry[]): TimeAxisPanelSpec {
    return {
        id: `category-panel-${unitCategory}`,
        type: PanelType.TimeAxis,
        axes: [categoryAxis(unitCategory, AxisSide.Left)],
        streams: streams.map((stream) => withAxisId(stream, unitCategory)),
    };
}

/** One panel per unit category, in order of first appearance. */
export function buildCategoryPanels(streams: StreamEntry[]): TimeAxisPanelSpec[] {
    return categoriesOf(streams).map((unitCategory) =>
        buildCategoryPanel(
            unitCategory,
            streams.filter((stream) => stream.axisId === unitCategory),
        ),
    );
}

/**
 * A panel per recipe whose categories are all present, and a category panel for every leftover,
 * each sitting where its first category falls in the stream order. A device missing part of a
 * recipe just gets category panels, same as before.
 */
export function buildRecipePanels(streams: StreamEntry[], recipes: PanelRecipe[]): TimeAxisPanelSpec[] {
    const present = new Set(streams.map((stream) => stream.axisId));
    const streamsIn = (unitCategory: string) => streams.filter((stream) => stream.axisId === unitCategory);
    const streamsOn = (side: string[] = []) => side.flatMap(streamsIn);

    const matched = recipes.filter((recipe) => recipeCategories(recipe).every((category) => present.has(category)));
    const recipeByCategory = new Map(
        matched.flatMap((recipe) => recipeCategories(recipe).map((category) => [category, recipe] as const)),
    );

    const built = new Set<PanelRecipe>();

    return categoriesOf(streams).flatMap((unitCategory) => {
        const recipe = recipeByCategory.get(unitCategory);
        if (!recipe) return buildCategoryPanel(unitCategory, streamsIn(unitCategory));
        if (built.has(recipe)) return [];

        built.add(recipe);
        return buildTwoAxisPanel(streamsOn(recipe.left), streamsOn(recipe.right)) ?? [];
    });
}

/** All streams on one panel, grouped by unit category with one left axis each. Exercises left y-axis selection. */
export function buildAllStreamsPanel(streams: StreamEntry[]): TimeAxisPanelSpec | null {
    if (streams.length === 0) return null;

    const unitCategories = categoriesOf(streams);

    return {
        id: "all-streams-panel",
        type: PanelType.TimeAxis,
        axes: unitCategories.map((unitCategory) => categoryAxis(unitCategory, AxisSide.Left)),
        streams: unitCategories.flatMap((unitCategory) => streams.filter((stream) => stream.axisId === unitCategory)),
    };
}

export function irrigationBand(
    zoneId: string,
    { position, plotHighlight }: Pick<BandSpec, "position" | "plotHighlight"> = {},
): BandSpec {
    return {
        kind: "scheduled-and-actual",
        scheduled: { kind: "legacy-zone-history", zoneId, signal: "scheduled" },
        actual: { kind: "legacy-zone-history", zoneId, signal: "valve-open" },
        ...(position && { position }),
        ...(plotHighlight && { plotHighlight }),
    };
}

/**
 * Panels and streams in the order the card's presentation list puts them, the categories it says
 * nothing about last.
 *
 * @see unitCategoryRank
 */
export function toStreamEntries(streams: ResolvedStream[], presentation: UnitCategoryPresentation[]): StreamEntry[] {
    const seenInCategory = new Map<string, number>();

    return [...streams]
        .sort((a, b) => unitCategoryRank(presentation, a.unitCategory) - unitCategoryRank(presentation, b.unitCategory))
        .map((stream) => {
            const indexInCategory = seenInCategory.get(stream.unitCategory) ?? 0;
            seenInCategory.set(stream.unitCategory, indexInCategory + 1);

            return toStreamEntry(stream, presentation, indexInCategory);
        });
}

/** @param indexInCategory - which stream this is among the ones sharing its category. Picks the color. */
export function toStreamEntry(
    stream: ResolvedStream,
    presentation: UnitCategoryPresentation[],
    indexInCategory: number,
): StreamEntry {
    return {
        streamId: stream.id,
        color: unitCategoryColor(presentation, stream.unitCategory, indexInCategory) ?? stableStreamColor(stream.id),
        name: stream.name,
        axisId: stream.unitCategory,
        unitCategory: stream.unitCategory,
        aggregate: stream.aggregate,
        ...(stream.sensorClusterName ? { sensorClusterName: stream.sensorClusterName } : {}),
        ...(stream.sensorElementName ? { sensorElementName: stream.sensorElementName } : {}),
        seriesType: getUnitCategoryConfiguration(stream.unitCategory)?.preferredSeriesType ?? "line",
    };
}
